import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Loader2, Clock, CalendarIcon, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, addDays, isPast, startOfDay, isToday } from 'date-fns';

interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
  conflictingEvent?: string;
}

interface AvailabilityResponse {
  success: boolean;
  surveyorEmail: string;
  surveyorName: string;
  date: string;
  slots: TimeSlot[];
  msGraphAvailable: boolean;
  error?: string;
}

interface Surveyor {
  name: string;
  email: string;
}

interface SurveyorAvailabilityPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSlotSelect: (date: Date, surveyorName: string) => void;
  initialSurveyor?: string;
  initialDate?: Date;
}

export function SurveyorAvailabilityPopup({
  open,
  onOpenChange,
  onSlotSelect,
  initialSurveyor,
  initialDate,
}: SurveyorAvailabilityPopupProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialDate || new Date()
  );
  const [selectedSurveyor, setSelectedSurveyor] = useState<string>(
    initialSurveyor || ''
  );
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);

  // Fetch available surveyors
  const { data: surveyorsData } = useQuery<{ success: boolean; surveyors: Surveyor[] }>({
    queryKey: ['/api/site-surveys/surveyors'],
    enabled: open,
  });

  // Fetch availability for selected surveyor and date
  const { data: availabilityData, isLoading: loadingAvailability, error: availabilityError } = useQuery<AvailabilityResponse>({
    queryKey: ['/api/site-surveys/availability', selectedSurveyor, format(selectedDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      const response = await fetch(
        `/api/site-surveys/availability/${encodeURIComponent(selectedSurveyor)}?date=${format(selectedDate, 'yyyy-MM-dd')}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch availability');
      }
      return response.json();
    },
    enabled: open && !!selectedSurveyor,
    // Calendars change between bookings; never offer slots from a cached list.
    staleTime: 0,
  });

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedSlot(null);
      if (initialSurveyor) {
        setSelectedSurveyor(initialSurveyor);
      }
      if (initialDate) {
        setSelectedDate(initialDate);
      }
    }
  }, [open, initialSurveyor, initialDate]);

  // Set initial surveyor when surveyors load
  useEffect(() => {
    if (surveyorsData?.surveyors?.length && !selectedSurveyor) {
      setSelectedSurveyor(surveyorsData.surveyors[0].name);
    }
  }, [surveyorsData, selectedSurveyor]);

  const handleConfirm = () => {
    if (selectedSlot && selectedSurveyor) {
      const slotDateTime = new Date(selectedSlot.start);
      onSlotSelect(slotDateTime, selectedSurveyor);
      onOpenChange(false);
    }
  };

  const formatTimeSlot = (slot: TimeSlot) => {
    const startTime = new Date(slot.start);
    const endTime = new Date(slot.end);
    return `${format(startTime, 'HH:mm')} - ${format(endTime, 'HH:mm')}`;
  };

  // Calculate the date range for the calendar (30 days ahead)
  const minDate = new Date();
  const maxDate = addDays(new Date(), 30);

  // Disable past dates
  const disabledDays = (date: Date) => {
    return isPast(startOfDay(date)) && !isToday(date);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Book Site Survey
          </DialogTitle>
          <DialogDescription>
            Select a surveyor, date, and time slot for the site survey
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          {/* Left side: Surveyor selection and Calendar */}
          <div className="space-y-4">
            {/* Surveyor Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Surveyor</label>
              <div className="flex gap-2">
                {surveyorsData?.surveyors?.map((surveyor) => (
                  <Button
                    key={surveyor.email}
                    variant={selectedSurveyor === surveyor.name ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => {
                      setSelectedSurveyor(surveyor.name);
                      setSelectedSlot(null);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    {surveyor.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Calendar */}
            <div className="border rounded-lg p-3">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(date);
                    setSelectedSlot(null);
                  }
                }}
                disabled={disabledDays}
                fromDate={minDate}
                toDate={maxDate}
                className="rounded-md"
              />
            </div>

            {/* Selected date display */}
            <div className="text-center text-sm text-muted-foreground">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </div>
          </div>

          {/* Right side: Time slots */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Available Times</label>
              {!availabilityData?.msGraphAvailable && availabilityData?.success && (
                <Badge variant="outline" className="text-xs">
                  Calendar offline - all slots shown
                </Badge>
              )}
            </div>

            <div className="border rounded-lg p-4 h-[240px] md:h-[340px] overflow-y-auto">
              {!selectedSurveyor ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Please select a surveyor
                </div>
              ) : loadingAvailability ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : availabilityError ? (
                <div className="flex items-center justify-center h-full text-red-500">
                  Failed to load availability
                </div>
              ) : availabilityData?.slots ? (
                <div className="grid grid-cols-2 gap-2">
                  {availabilityData.slots.map((slot, index) => {
                    const isSelected = selectedSlot?.start === slot.start;
                    const isPastSlot = new Date(slot.start) < new Date();

                    return (
                      <Button
                        key={index}
                        variant={isSelected ? 'default' : 'outline'}
                        className={cn(
                          'h-auto py-3 flex flex-col items-center justify-center gap-1',
                          !slot.available && 'opacity-50 cursor-not-allowed',
                          isPastSlot && 'opacity-30 cursor-not-allowed'
                        )}
                        disabled={!slot.available || isPastSlot}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <Clock className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {format(new Date(slot.start), 'HH:mm')}
                        </span>
                        {!slot.available && slot.conflictingEvent && (
                          <span className="text-xs text-muted-foreground truncate max-w-full">
                            {slot.conflictingEvent}
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No slots available
                </div>
              )}
            </div>

            {/* Selection summary and confirm button */}
            {selectedSlot && (
              <div className="space-y-3 pt-2 border-t">
                <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium">Selected Appointment</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedSurveyor} - {format(selectedDate, 'EEE, MMM d')} at {format(new Date(selectedSlot.start), 'HH:mm')}
                  </p>
                </div>
                <Button
                  className="w-full"
                  onClick={handleConfirm}
                  disabled={!selectedSlot}
                >
                  Confirm Booking
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
